import React, { useState } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { FaCheck, FaCreditCard, FaQuestionCircle } from "react-icons/fa";
import "./Styles.css";

const ELEMENT_STYLE = {
  base: {
    color: "#172033",
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSmoothing: "antialiased",
    fontSize: "16px",
    fontWeight: "500",
    "::placeholder": { color: "#94a3b8" },
  },
  invalid: { color: "#b42318", iconColor: "#b42318" },
};

const ELEMENT_OPTIONS = {
  number: { style: ELEMENT_STYLE, placeholder: "1234 5678 9012 3456", showIcon: true },
  expiry: { style: ELEMENT_STYLE, placeholder: "MM / YY" },
  cvc: { style: ELEMENT_STYLE, placeholder: "CVC" },
};

const StripeField = ({ id, label, error, complete, focused, children, leadingIcon, help }) => {
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;

  return (
    <div
      data-testid={`${id}-field`}
      className={`wallet-payment-field${error ? " wallet-payment-field--error" : ""}${complete && !error ? " wallet-payment-field--complete" : ""}${focused ? " wallet-payment-field--focus" : ""}`}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={error ? errorId : undefined}
    >
      <div className="wallet-payment-field__label-row">
        <span id={labelId} className="wallet-payment-field__label">{label}</span>
        {help}
      </div>
      <div className="wallet-payment-stripe-control">
        {leadingIcon && <span className="wallet-payment-stripe-control__leading" aria-hidden="true">{leadingIcon}</span>}
        <div className="wallet-payment-stripe-control__element">{children}</div>
        {complete && !error && <FaCheck className="wallet-payment-stripe-control__complete" aria-label="Complete" />}
      </div>
      <div id={errorId} className="wallet-payment-field__message" aria-live="polite">
        {error && <span role="alert">{error}</span>}
      </div>
    </div>
  );
};

export default function CardSection({ text, errors, complete, onChange }) {
  const [showCvcHelp, setShowCvcHelp] = useState(false);
  const [focusedField, setFocusedField] = useState("");

  const cvcHelp = (
    <span className="wallet-payment-cvc-help">
      <button
        type="button"
        className="wallet-payment-cvc-help__button"
        aria-label={text("securityCodeHelpLabel", "What is a security code?")}
        aria-expanded={showCvcHelp}
        aria-controls="wallet-payment-cvc-help-text"
        onClick={() => setShowCvcHelp((current) => !current)}
      >
        <FaQuestionCircle aria-hidden="true" />
      </button>
      <span
        id="wallet-payment-cvc-help-text"
        role="tooltip"
        aria-hidden={!showCvcHelp}
        className={`wallet-payment-cvc-help__popover${showCvcHelp ? " wallet-payment-cvc-help__popover--visible" : ""}`}
      >
        {text("securityCodeHelp", "3 or 4-digit security code found on your card.")}
      </span>
    </span>
  );

  return (
    <div className="wallet-payment-card-fields">
      <StripeField
        id="stripe-card-number"
        label={text("cardNumber", "Card number")}
        error={errors.number}
        complete={complete.number}
        focused={focusedField === "number"}
        leadingIcon={<FaCreditCard />}
      >
        <CardNumberElement
          id="stripe-card-number-element"
          options={ELEMENT_OPTIONS.number}
          onChange={(event) => onChange("number", event)}
          onFocus={() => setFocusedField("number")}
          onBlur={() => setFocusedField("")}
        />
      </StripeField>

      <div className="wallet-payment-card-row">
        <StripeField
          id="stripe-card-expiry"
          label={text("expirationDate", "Expiration date")}
          error={errors.expiry}
          complete={complete.expiry}
          focused={focusedField === "expiry"}
        >
          <CardExpiryElement
            id="stripe-card-expiry-element"
            options={ELEMENT_OPTIONS.expiry}
            onChange={(event) => onChange("expiry", event)}
            onFocus={() => setFocusedField("expiry")}
            onBlur={() => setFocusedField("")}
          />
        </StripeField>
        <StripeField
          id="stripe-card-cvc"
          label={text("securityCode", "Security code")}
          error={errors.cvc}
          complete={complete.cvc}
          focused={focusedField === "cvc"}
          help={cvcHelp}
        >
          <CardCvcElement
            id="stripe-card-cvc-element"
            options={ELEMENT_OPTIONS.cvc}
            onChange={(event) => onChange("cvc", event)}
            onFocus={() => setFocusedField("cvc")}
            onBlur={() => setFocusedField("")}
          />
        </StripeField>
      </div>
    </div>
  );
}
